import React from 'react';
import moment from 'moment';
import * as XLSX from 'xlsx';
import handleFileExport from '@vendys-frontend/excel-export';
import { UploadOutlined, CloseCircleFilled } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { useSaveMealPlanBulk } from 'hooks';

import MealPlanUploadExcelLayout from '../excelLayout/MealPlanUploadExcelLayout.json';
import {
  createCategoryData,
  createTargetExcelData,
  createColumnValidations,
  createMealPlanFormulas,
  createMealPlanItemFormulas
} from './schema';
import { StyledModal, ExcelUploadInput, FileUploadModalFooter, FileUploadModalContent } from './styles';

const accept = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const mealPlanHeaderMap = {
  '[필수]등록일자': 'serveDate',
  '[필수]만족도평가사용': 'isUseSatisfactionSurvey',
  '[필수]식단구성사용': 'isUseMealPlanItem',
  '[필수]식단명': 'mealPlanName',
  '[필수]카테고리': 'categoryName',
  번호: 'localIndex',
  '원산지 구성': 'content',
  '카테고리 코드(자동입력)': 'categoryId'
};
const mealPlanItemHeaderMap = {
  '식단표 번호': 'localIndex',
  '[필수] 식단구성명': 'mealPlanItemName',
  '알레르기 구성': 'description',
  '칼로리 (Kcal)': 'calories',
  '탄수화물(g)': 'carbohydrate',
  '단백질(g)': 'protein',
  '지방(g)': 'fat',
  '나트륨(mg)': 'natrium',
  '당류(g)': 'sugar'
};

interface MealPlanExcelUploadModalProps {
  open: boolean;
  categories: { categoryId: string; categoryName: string }[];
  onCancel?: () => void;
  onConfirm?: () => void;
}

const MealPlanExcelUploadModal = ({ open, categories, onCancel, onConfirm }: MealPlanExcelUploadModalProps) => {
  const excelInputRef = React.useRef<HTMLInputElement>();
  const [mealPlans, setMealPlans] = React.useState([]);
  const [excelFileName, setExcelFileName] = React.useState();

  const { mutateAsync: saveMealPlanBulk } = useSaveMealPlanBulk();

  const handleExcelFileDownload = () => {
    const fileName = `식단표 등록 양식_${moment().valueOf()}`;
    const { categoryDatas, categoryNames } = createCategoryData(categories);
    const targetExcelDatas = createTargetExcelData();
    const colValidations = createColumnValidations(categoryNames);
    const mealPlanFormulas = createMealPlanFormulas();
    const mealPlanItemFormulas = createMealPlanItemFormulas();

    const EXCEL_START_ROW = 1;
    const sheetData = {
      '0': {
        startRowNum: 3,
        newRows: targetExcelDatas,
        customFormulas: mealPlanFormulas,
        colValidations
      },
      '1': {
        startRowNum: 4,
        newRows: Array(300)
          .fill(0)
          .map(() => []),
        customFormulas: mealPlanItemFormulas,
        colValidations: {
          [EXCEL_START_ROW]: {
            minRowNumber: EXCEL_START_ROW.toString(),
            dataValidation: {
              type: 'list',
              formulae: [
                `"${Array(30)
                  .fill(0)
                  .map((_, index) => index + 1)
                  .join()}"`
              ],
              allowBlank: true,
              showInputMessage: true,
              showErrorMessage: true,
              errorTitle: 'Error',
              error: '올바른 값을 선택해주세요'
            }
          }
        }
      },
      '2': {
        tableInfos: [
          {
            name: 'categoryTable',
            ref: 'A1',
            style: { theme: 'TableStyleLight1' },
            columns: [{ name: '카테고리 명' }, { name: '카테고리 코드' }],
            rows: categoryDatas
          }
        ]
      }
    };
    handleFileExport(MealPlanUploadExcelLayout, sheetData, fileName);
  };

  const fileUploadOnClick = () => {
    excelInputRef?.current?.click();
  };

  const parseExcelRow = (row: unknown[], headerRow: string[], headerMap: Record<string, string>) => {
    const obj: Record<string, unknown> = {};
    for (let i = 0; i < headerRow.length; i += 1) {
      obj[headerMap[headerRow[i]]] = row[i];
    }
    return obj;
  };

  const processMealPlanSheet = (workbook: XLSX.WorkBook) => {
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    const headerRow = jsonData[0] as string[];

    const resultArray = [];

    jsonData?.slice(2).forEach((row: unknown[]) => {
      const obj = parseExcelRow(row, headerRow, mealPlanHeaderMap);

      if (obj.mealPlanName) {
        resultArray.push({
          ...obj,
          serveDate: moment('1899-12-30')
            .add(obj?.serveDate ?? 0 - 1, 'days')
            .format('YYYY-MM-DD'),
          isUseMealPlanItem: obj?.isUseMealPlanItem === '사용',
          isUseSatisfactionSurvey: obj?.isUseSatisfactionSurvey === '사용',
          items: []
        });
      }
    });
    return resultArray;
  };

  const processMealPlanItemSheet = (workbook: XLSX.WorkBook) => {
    const sheetName = workbook.SheetNames[1];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    const headerRow = jsonData[0] as string[];

    const resultArray = [];

    jsonData?.slice(3).forEach((row: unknown[], index) => {
      const obj = parseExcelRow(row, headerRow, mealPlanItemHeaderMap);
      if (obj.localIndex && obj.mealPlanItemName) {
        resultArray.push({ ...obj, sortBy: index + 1 });
      }
    });
    return resultArray;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();

      reader.onload = ({ target }) => {
        const arrayBuffer = target?.result;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        const mealPlanData = processMealPlanSheet(workbook);
        const mealPlanItemData = processMealPlanItemSheet(workbook);

        const resultArray = mealPlanData.map((mealPlan) => {
          const items = mealPlanItemData.filter((item) => mealPlan.localIndex === item.localIndex);
          return { ...mealPlan, items };
        });

        setMealPlans(resultArray);
        setExcelFileName(file.name);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleEacelUpload = async () => {
    await saveMealPlanBulk({ mealPlans });
    Modal.info({
      className: 'info-handler-modal',
      content: `식단표가 일괄 등록되었습니다.`,
      icon: null,
      okText: '확인',
      onOk: onConfirm
    });
  };

  const handleRemoteExcelFile = () => {
    setMealPlans([]);
    setExcelFileName(null);
  };

  return (
    <StyledModal
      open={open}
      title="엑셀 파일로 일괄 업로드"
      footer={[
        <FileUploadModalFooter>
          <Button onClick={() => onCancel?.()}>취소</Button>
          <Button type="primary" onClick={handleExcelFileDownload}>
            엑셀 양식 다운로드
          </Button>
          <Button type="primary" onClick={handleEacelUpload}>
            일괄 등록
          </Button>
        </FileUploadModalFooter>
      ]}
      onCancel={() => onCancel?.()}
    >
      <FileUploadModalContent>
        <ul>
          <li>최대 30개까지 한 번에 등록 가능합니다.</li>
          <li>각 식단표의 순서는 자동으로 등록됩니다. 등록이후에 드래그 앤 드랍으로 수정해주세요.</li>
          <li>이미지 파일은 등록 이후 각 식단표별로 개별 적용해야 합니다.</li>
        </ul>
        {!excelFileName ? <input type="file" accept={accept} ref={excelInputRef} onChange={handleFileChange} /> : null}
        {!excelFileName ? (
          <ExcelUploadInput onClick={fileUploadOnClick}>
            <UploadOutlined />
            <div>파일 업로드</div>
          </ExcelUploadInput>
        ) : null}
        {excelFileName ? (
          <ExcelUploadInput>
            <CloseCircleFilled onClick={handleRemoteExcelFile} />
            <div className="file-name">{excelFileName}</div>
          </ExcelUploadInput>
        ) : null}
      </FileUploadModalContent>
    </StyledModal>
  );
};

export default MealPlanExcelUploadModal;
